/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useToggle } from '@tonic-one/react-hooks';
import CSpinner from '../../../components/CSpinner';
import { useEffect, useRef, useState } from 'react';
import { Steps } from 'antd';
import { ProvisionOrgInfo, StepIcon } from '../ProvisionModal/share';
import { Trans, useTranslation } from 'react-i18next';
import { deleteCookie, getProvisionCookie } from '@Utils/common';
import { ProvisionApi } from '@Apis';
import { reportPendo } from '@Utils/setupPendo';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import { MultiAppsAuthenticationInfoRes } from '@Types/provision';
import { App, Service } from '@Types/app';
import { CAS_DOC_LINK, getCasOLHLink } from '@Utils/casDocLink';
import i18next from 'i18next';

enum OneDriveStatus {
  NotProvision = 0,
  ProvisioningGrantStep1Done = 1,
  ProvisioningGrantStep2Done = 2,
}

const OneDriveGraphReAuthModal = ({
  orgId,
  onClose,
  authAccount,
}: {
  orgId: string;
  authAccount: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation('provision');
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

  const [onedriveStatus, setOnedriveStatus] = useState<OneDriveStatus>(
    OneDriveStatus.NotProvision
  );

  const [authenticationInfo, setAuthenticationInfo] =
    useState<null | MultiAppsAuthenticationInfoRes>(null);

  useEffect(() => {
    if (
      onedriveStatus === OneDriveStatus.NotProvision ||
      onedriveStatus === OneDriveStatus.ProvisioningGrantStep1Done
    ) {
      ProvisionApi.apiGetMultiAppsReAuthAuthenticationInfos(
        Service.OneDrive,
        orgId
      ).then((res) => {
        setAuthenticationInfo(res.data);
      });
    }
  }, []);

  if (!authenticationInfo) {
    return <></>;
  }
  return (
    <>
      <Modal isOpen={true} isClosable onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t(`provision.reAuth.service.oneDrive.title`)}
          </ModalHeader>

          <ModalBody>
            <ProvisionOrgInfo orgId={orgId} />

            {errorMsg && (
              <ErrorAlert
                errorMsg={errorMsg}
                onClose={() => setErrorMsg(undefined)}
              />
            )}
            <GrantPermissionSteps
              {...{
                onedriveStatus,
                setOnedriveStatus,
                authenticationInfo,
                orgId,
                setErrorMsg,
                authAccount,
              }}
            />
          </ModalBody>
          <ModalFooter>
            <Button minWidth="20x" onClick={onClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default OneDriveGraphReAuthModal;

const GrantPermissionSteps = ({
  onedriveStatus,
  setOnedriveStatus,
  authenticationInfo,
  orgId,
  setErrorMsg,
  authAccount,
}: {
  onedriveStatus: OneDriveStatus;
  setOnedriveStatus: (status: OneDriveStatus) => void;
  authenticationInfo: MultiAppsAuthenticationInfoRes;
  orgId: string;
  setErrorMsg: (msg: string | undefined) => void;
  authAccount: string;
}) => {
  const { t } = useTranslation(['provision', 'common']);
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const titleTextStyle = {
    fontSize: '16px',
    lineHeight: '22px',
    fontWeight: '400',
    color: colorStyle.color.primary,
  };
  const [loading, toggleLoading] = useToggle(false);
  const timeRef = useRef<any>(null);

  const onFirstPermission = () => {
    const authUrl = authenticationInfo[0].authenticationUrl;
    if (!authUrl) {
      return;
    }
    setErrorMsg(undefined);
    reportPendo('ReAuth.GrantPermission.Click', {
      orgId,
      service: App.OneDrive,
    });
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const onedriveProvisionInfo = getProvisionCookie(
        'provision_status_azuread_graph_od'
      );
      if (onedriveProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code } = JSON.parse(onedriveProvisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res = await ProvisionApi.apiSaveReAuthInfo(
              App.OneDrive,
              code,
              authAccount,
              orgId
            );
            if (res.data?.status === 'failure') {
              setErrorMsg(
                t([
                  `provision.service.oneDrive.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              setOnedriveStatus(OneDriveStatus.ProvisioningGrantStep1Done);
            }
          } catch {
            setErrorMsg(t('common.apiException.internalError'));
          }
        } else {
          setErrorMsg(t('provision.service.oneDrive.error.deniedGrant'));
        }
        toggleLoading(false);
      }
    }, 1000);
  };
  const onSecondPermission = () => {
    const authUrl = authenticationInfo[1].authenticationUrl;
    if (!authUrl) {
      return;
    }
    setErrorMsg(undefined);
    reportPendo('ReAuth.GrantPermission.Click', {
      orgId,
      service: App.OneDriveBasic,
    });
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const onedriveBasicProvisionInfo = getProvisionCookie(
        'provision_status_azuread_graph_basic_od'
      );
      if (onedriveBasicProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code } = JSON.parse(onedriveBasicProvisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res = await ProvisionApi.apiSaveReAuthInfo(
              App.OneDriveBasic,
              code,
              authAccount,
              orgId
            );
            if (res.data?.status === 'failure') {
              setErrorMsg(
                t([
                  `provision.service.oneDrive.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              setOnedriveStatus(OneDriveStatus.ProvisioningGrantStep2Done);
            }
          } catch {
            setErrorMsg(t('common.apiException.internalError'));
          }
        } else {
          setErrorMsg(t('provision.service.oneDrive.error.deniedGrant'));
        }
        toggleLoading(false);
      }
    }, 1000);
  };

  useEffect(() => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
      }
    };
  }, []);

  return (
    <>
      {loading && <CSpinner />}
      <Steps
        direction="vertical"
        current={onedriveStatus - 1}
        items={[
          {
            title: (
              <Text {...titleTextStyle}>
                <Trans
                  i18nKey="provision.reAuth.service.oneDrive.details.userConfiguration.STEP1"
                  ns="provision"
                >
                  placeholder
                  <Link
                    href={getCasOLHLink(
                      CAS_DOC_LINK.provision.oneDrive.userConfiguration,
                      i18next.language
                    )}
                    target="_blank"
                  >
                    placeholder
                  </Link>
                  placeholder
                </Trans>
              </Text>
            ),
            description: (
              <Flex direction="column" pt="16px" gap="16px">
                <Box>
                  {onedriveStatus <
                  OneDriveStatus.ProvisioningGrantStep1Done ? (
                    <Button
                      variant="secondary"
                      borderRadius="2rem"
                      onClick={() => {
                        onFirstPermission();
                      }}
                    >
                      {t('provision.button.grantPermission')}
                    </Button>
                  ) : (
                    <Text color={colorStyle.color.tertiary}>
                      {t('provision.common.permissionGranted')}
                    </Text>
                  )}{' '}
                </Box>
              </Flex>
            ),
            icon: (
              <StepIcon
                checked={
                  onedriveStatus >= OneDriveStatus.ProvisioningGrantStep1Done
                }
              />
            ),
          },
          {
            title: (
              <Text color={colorStyle.color.primary}>
                {t(
                  'provision.reAuth.service.oneDrive.details.userConfiguration.STEP2'
                )}
              </Text>
            ),
            description: (
              <Flex direction="column" pt="16px" gap="16px">
                <Box>
                  {onedriveStatus <
                  OneDriveStatus.ProvisioningGrantStep2Done ? (
                    <Button
                      variant="secondary"
                      borderRadius="2rem"
                      disabled={
                        !(
                          onedriveStatus ===
                          OneDriveStatus.ProvisioningGrantStep1Done
                        )
                      }
                      onClick={() => {
                        onSecondPermission();
                      }}
                    >
                      {t('provision.button.grantPermission')}
                    </Button>
                  ) : (
                    <Text color={colorStyle.color.tertiary}>
                      {t('provision.common.permissionGranted')}
                    </Text>
                  )}
                </Box>
              </Flex>
            ),
            icon: (
              <StepIcon
                checked={
                  onedriveStatus >= OneDriveStatus.ProvisioningGrantStep2Done
                }
              />
            ),
          },
        ]}
      />
    </>
  );
};
